import React, { useState, useEffect } from 'react';
import { useRoute, useLocation } from 'wouter';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Info, Lock, Eye, Play, Plus, AlertTriangle, Hash, ArrowLeft, Loader2, CheckCircle, CircleSlash } from 'lucide-react';
import { Button } from '../ui/button';
import { getJson } from '@/lib/http';
import { useAuth } from '@/lib/auth-store';
import { PackLockingPanel } from './PackLockingPanel';
import { PackPreviewPanel } from '../pack-preview/PackPreviewPanel';
import { CampaignSimulationPanel } from '../campaign-simulation/CampaignSimulationPanel';
import { CampaignCreationPanel } from '../campaign-creation/CampaignCreationPanel';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';

interface PackMetaResponse {
  name: string;
  retailer: { id: number; name: string };
  status: string;
}

interface PackLockStatusResponse {
  versions: Array<{ id: number; status: string }>;
}

export const PackLockingDashboard: React.FC = () => {
  const { state } = useAuth();

  const [, setLocation] = useLocation();
  const [, params] = useRoute('/pack-locking/:packId');
  const packId = params?.packId;
  const [isLocked, setIsLocked] = useState(false);
  const [lockedVersionId, setLockedVersionId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('locking');
  const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);
  const [showIds, setShowIds] = useState(false);
  const [packMeta, setPackMeta] = useState<{
    name: string;
    retailer: { id: number; name: string };
    status: string;
  } | null>(null);

  // Load show IDs preference from localStorage
  useEffect(() => {
    const savedShowIds = localStorage.getItem('packLocking.showIds');
    if (savedShowIds !== null) {
      setShowIds(savedShowIds === 'true');
    }
  }, []);

  // Save show IDs preference to localStorage
  const handleShowIdsChange = (checked: boolean) => {
    setShowIds(checked);
    localStorage.setItem('packLocking.showIds', checked.toString());
  };

  // ID validation - allow numeric OR UUID
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(packId || '');
  const isNumeric = /^\d+$/.test(packId || '');
  const isValidPackId = !!packId && (isUuid || isNumeric);

  // Convert packId to appropriate type for child components
  const numericPackId = isNumeric ? parseInt(packId!, 10) : undefined;
  const uuidPackId = isUuid ? packId! : undefined;

  // Check feature flag on component mount
  useEffect(() => {
    const checkFeatureFlag = async () => {
      try {
        const response = await fetch('/api/_health/pack-lock-preview');
        setFeatureEnabled(response.ok);
      } catch (error) {
        setFeatureEnabled(false);
      }
    };
    
    checkFeatureFlag();
  }, []);

  // Fetch pack metadata on component mount
  useEffect(() => {
    const fetchPackMeta = async () => {
      if (!packId) return;
      
      try {
        const data = await getJson<PackMetaResponse>(`/api/packs/${packId}/meta`);
        if (data) setPackMeta(data);
      } catch (error) {
        console.error('Failed to fetch pack metadata:', error);
      }
    };
    
    // Only proceed when authenticated so Authorization is attached
    if (state.status === 'authenticated') {
      fetchPackMeta();
      // Also check lock status on mount
      checkLockStatus();
    }
    
    // Set up periodic refresh of lock status
    const interval = setInterval(() => {
      if (state.status === 'authenticated') checkLockStatus();
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(interval);
  }, [packId, state.status]);

  // Function to check lock status
  const checkLockStatus = async () => {
    if (!packId) return;
    
    try {
      const data = await getJson<PackLockStatusResponse>(`/api/pack-locking/${packId}/lock-status`);
      const lockedVersion = data?.versions?.find((v: any) => v.status === 'locked');
      const isCurrentlyLocked = !!lockedVersion;
      console.log('[PACK_LOCK_STATUS_DEBUG]', { packId, response: data, lockedVersionId: lockedVersion?.id ?? null, isLocked: isCurrentlyLocked });
      if (isCurrentlyLocked !== isLocked) {
        setIsLocked(isCurrentlyLocked);
        setLockedVersionId(lockedVersion?.id ?? null);
      }
    } catch (error) {
      console.error('Failed to check lock status:', error);
    }
  };

  // Redirect if no pack ID or invalid format
  useEffect(() => {
    if (!isValidPackId) {
      setLocation('/pack-management');
    }
  }, [isValidPackId, setLocation]);

  // Show feature disabled message if feature flag is off
  if (featureEnabled === false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Lock className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Feature Disabled</h2>
          <p className="text-muted-foreground mb-4">
            Pack Locking & Preview feature is currently disabled by server configuration.
          </p>
          <button 
            onClick={() => setLocation('/pack-management')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Pack Management
          </button>
        </div>
      </div>
    );
  }

  if (!isValidPackId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Invalid Pack ID</h2>
          <p className="text-muted-foreground mb-4">
            The pack ID must be a valid number or UUID format.
          </p>
          <button 
            onClick={() => setLocation('/pack-management')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Pack Management
          </button>
        </div>
      </div>
    );
  }

  const handleLockStatusChange = (locked: boolean, versionId?: number) => {
    setIsLocked(locked);
    if (locked && versionId) {
      setLockedVersionId(versionId);
    } else if (!locked) {
      setLockedVersionId(null);
    }
  };

  const handleCampaignCreated = (campaign: any) => {
    // Handle campaign creation success
    console.log('Campaign created:', campaign);
    // Could trigger navigation or other actions
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setLocation('/pack-management')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Packs
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Pack Locking & Campaign Preview</h1>
            <p className="text-muted-foreground">
              {packMeta ? (
                <>
                  Lock packs, preview campaigns, and simulate execution for{' '}
                  <span className="font-medium">{packMeta.name}</span>
                  {packMeta.retailer && (
                    <span className="text-sm bg-gray-100 px-2 py-1 rounded ml-2">
                      {packMeta.retailer.name}
                    </span>
                  )}
                </>
              ) : (
                `Lock packs, preview campaigns, and simulate execution for pack ${packId}`
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isLocked ? "default" : "secondary"} className="flex items-center gap-1">
            {isLocked ? <CheckCircle className="h-3 w-3 text-green-600" /> : <CircleSlash className="h-3 w-3 text-gray-500" />}
            {isLocked ? "Locked" : "Unlocked"}
          </Badge>
          {lockedVersionId && (
            <Badge variant="outline">Version {lockedVersionId}</Badge>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={checkLockStatus}
            className="flex items-center gap-2"
          >
            <Loader2 className="h-3 w-3" />
            Refresh Status
          </Button>
          <Switch id="show-ids" checked={showIds} onCheckedChange={handleShowIdsChange} />
          <Label htmlFor="show-ids">Show IDs</Label>
        </div>
      </div>

      {/* Feature Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Feature Status
          </CardTitle>
          <CardDescription>
            Current status of Pack Locking and Campaign Simulation features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 border rounded-lg">
              <Lock className="h-6 w-6 mx-auto mb-2 text-blue-600" />
              <div className="text-sm font-medium">Pack Locking</div>
              <Badge variant="default" className="mt-1">Enabled</Badge>
            </div>
            <div className="text-center p-3 border rounded-lg">
              <Eye className="h-6 w-6 mx-auto mb-2 text-green-600" />
              <div className="text-sm font-medium">Pack Preview</div>
              <Badge variant="default" className="mt-1">Enabled</Badge>
            </div>
            <div className="text-center p-3 border rounded-lg">
              <Play className="h-6 w-6 mx-auto mb-2 text-purple-600" />
              <div className="text-sm font-medium">Campaign Simulation</div>
              <Badge variant="default" className="mt-1">Enabled</Badge>
            </div>
            <div className="text-center p-3 border rounded-lg">
              <Plus className="h-6 w-6 mx-auto mb-2 text-orange-600" />
              <div className="text-sm font-medium">Campaign Creation</div>
              <Badge variant="default" className="mt-1">Enabled</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="locking" className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Pack Locking
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="simulation" className="flex items-center gap-2" disabled={!isLocked}>
            <Play className="h-4 w-4" />
            Simulation
          </TabsTrigger>
          <TabsTrigger value="creation" className="flex items-center gap-2" disabled={!isLocked}>
            <Plus className="h-4 w-4" />
            Create Campaign
          </TabsTrigger>
        </TabsList>

        {/* Pack Locking Tab */}
        <TabsContent value="locking" className="space-y-4">
          <PackLockingPanel 
            packId={numericPackId || 0} 
            onLockStatusChange={(locked) => handleLockStatusChange(locked)}
            showIds={showIds}
          />
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-4">
          <PackPreviewPanel 
            packId={numericPackId || 0} 
            showIds={showIds}
            onOperatorAssignmentChange={() => {
              // Refresh the locking panel when operator assignments change
              // This will update the blockers and validation status
              const lockingPanel = document.querySelector('[data-pack-locking-panel]');
              if (lockingPanel) {
                // Trigger a refresh by updating the key or calling a refresh method
                // For now, we'll force a re-render by updating state
                setActiveTab(activeTab); // This will trigger a re-render
              }
            }}
          />
        </TabsContent>

        {/* Simulation Tab */}
        <TabsContent value="simulation" className="space-y-4">
          <CampaignSimulationPanel 
            packVersionId={lockedVersionId || 0} 
          />
        </TabsContent>

        {/* Campaign Creation Tab */}
        <TabsContent value="creation" className="space-y-4">
          <CampaignCreationPanel 
            packVersionId={lockedVersionId || 0} 
            onCampaignCreated={handleCampaignCreated}
          />
        </TabsContent>
      </Tabs>

      {/* Workflow Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Workflow Guide</CardTitle>
          <CardDescription>
            Follow these steps to use the Pack Locking and Campaign Preview features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium mx-auto mb-2">
                  1
                </div>
                <h4 className="font-medium">Lock Pack</h4>
                <p className="text-sm text-muted-foreground">
                  Validate and lock the pack to create an immutable version
                </p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium mx-auto mb-2">
                  2
                </div>
                <h4 className="font-medium">Preview</h4>
                <p className="text-sm text-muted-foreground">
                  Review the locked version and compare with draft
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feature Flags Info */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Feature Flags:</strong> This dashboard is controlled by the{' '}
          <code className="bg-muted px-1 rounded">PACK_LOCK_PREVIEW</code> environment variable.
          All features are currently enabled for development and testing.
        </AlertDescription>
      </Alert>
    </div>
  );
};
